// Hook for detecting and applying PWA service worker updates with navigation-based update checks

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useRegisterSW } from 'virtual:pwa-register/react'

export function usePWAUpdate() {
  const location = useLocation()
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration
    },
    onRegisterError(error) {
      console.error('SW registration error', error)
    },
  })

  // Check for updates on each navigation (locked decision)
  useEffect(() => {
    const reg = registrationRef.current
    if (reg && navigator.onLine) {
      reg.update().catch(() => {
        // Silently ignore — user may be offline
      })
    }
  }, [location.pathname])

  const dismissUpdate = () => {
    // Temporary dismiss — reappears on next navigation when registration.update() re-detects waiting worker
    setNeedRefresh(false)
  }

  const applyUpdate = async () => {
    // Posts SKIP_WAITING to the waiting SW. Once it activates and takes
    // control, vite-plugin-pwa's internal controllerchange listener reloads
    // the page onto the new version. A manual reload here would race that
    // activation and reload while the old SW still controls the page.
    await updateServiceWorker(true)
  }

  return { needRefresh, dismissUpdate, applyUpdate }
}
